import React, { useState, useEffect } from "react";
import { v4 as uuidv4 } from "uuid";
// Import PDF generation libraries
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { useAuth } from "../auth/AuthContext";
import {
  TextField,
  Button,
  IconButton,
  Box,
  Typography,
  Alert,
  Snackbar,
  FormControl,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Select,
  MenuItem,
} from "@mui/material";
import {
  Add,
  Delete,
  AttachFile,
  CloudUpload,
  PictureAsPdf,
} from "@mui/icons-material";
import { doc, updateDoc, addDoc, collection } from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { firestore as db } from "../components/firebase.config";
// ❌ REMOVED: import Cookies from "js-cookie";

const ChecklistForm = ({
  email,
  onClose,
  selectedChecklist,
  isAssignedChecklist = false,
  readOnlyStructure = false,
  hideStructureControls = false,
}) => {
  const [checklistName, setChecklistName] = useState("");
  const [description, setDescription] = useState("");
  const [remarks, setRemarks] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [columns, setColumns] = useState([]);
  const [sections, setSections] = useState([]);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("success");
  const [currentRowId, setCurrentRowId] = useState(null);
  const [currentColumnId, setCurrentColumnId] = useState(null);
  const [attachmentDialogOpen, setAttachmentDialogOpen] = useState(false);
  const [attachmentUrl, setAttachmentUrl] = useState("");
  const [attachmentType, setAttachmentType] = useState("url"); // "url", "image", or "file"
  const [uploadedImage, setUploadedImage] = useState(null);
  const [isListening, setIsListening] = useState(false);
  const [recognition, setRecognition] = useState(null);
  const [currentTranscript, setCurrentTranscript] = useState("");
  const [currentCell, setCurrentCell] = useState(null);

  const storage = getStorage();
  const { currentUser, tenantId, isSuperAdmin } = useAuth();

  const shouldHideStructureControls =
    hideStructureControls || readOnlyStructure || isAssignedChecklist;

  useEffect(() => {
    if (selectedChecklist) {
      setChecklistName(selectedChecklist.name || "");
      setDescription(selectedChecklist.description || "");
      setRemarks(selectedChecklist.remarks || "");
      setImageUrl(selectedChecklist.imageUrl || "");
      setColumns(selectedChecklist.columns || []);

      if (selectedChecklist.sections) {
        setSections(selectedChecklist.sections);
      } else if (selectedChecklist.rows) {
        const convertedSections = [];
        let currentSection = null;

        selectedChecklist.rows.forEach((row) => {
          if (row.data && row.data.isSectionHeader) {
            if (currentSection) {
              convertedSections.push(currentSection);
            }
            currentSection = {
              id: uuidv4(),
              title: row.data.sectionTitle || "Untitled Section",
              type: row.data.sectionType || "A",
              rows: [],
            };
          } else if (currentSection) {
            currentSection.rows.push(row);
          } else {
            currentSection = {
              id: uuidv4(),
              title: "General",
              type: "A",
              rows: [row],
            };
          }
        });

        if (currentSection) {
          convertedSections.push(currentSection);
        }

        setSections(convertedSections);
      } else {
        setSections([
          {
            id: uuidv4(),
            title: "General Section",
            type: "A",
            rows: [{ id: uuidv4(), data: {} }],
          },
        ]);
      }
    } else {
      // Initialize with default columns and one empty section
      setColumns([
        { id: uuidv4(), name: "Sr#" },
        { id: uuidv4(), name: "MMES Requirements of 3P" },
        { id: uuidv4(), name: "Guideline prompts" },
        { id: uuidv4(), name: "Observation" },
        { id: uuidv4(), name: "Compliance details" },
        { id: uuidv4(), name: "Suggestion" },
        { id: uuidv4(), name: "Max. score" },
        { id: uuidv4(), name: "Audit score" },
        { id: uuidv4(), name: "Risk Zone R/Y/G" },

        { id: uuidv4(), name: "Attachments" },
      ]);

      // Start with one empty section that users can customize
      setSections([
        {
          id: uuidv4(),
          title: "General Section",
          type: "A",
          rows: [{ id: uuidv4(), data: {} }],
        },
      ]);
    }
  }, [selectedChecklist]);

  const handleExportPdf = () => {
    // 1. Initialize a new jsPDF document
    const doc = new jsPDF({
      orientation: "landscape", // Use landscape for wide tables
    });

    // 2. Define the columns for your table.
    const pdfColumns = columns.map((col) => ({
      header: col.name,
      dataKey: col.id,
    }));

    // 3. Prepare the body data for the PDF table from the component's state.
    const pdfBody = [];
    sections.forEach((section) => {
      // Add a special row for the section header.
      const sectionHeaderRow = {
        isSectionHeader: true,
        content: `${section.type}. ${section.title}`,
      };
      // Put the content in the first column's dataKey and empty strings for others
      pdfColumns.forEach((col, index) => {
        if (index === 0) {
          sectionHeaderRow[col.dataKey] = sectionHeaderRow.content;
        } else {
          sectionHeaderRow[col.dataKey] = "";
        }
      });
      pdfBody.push(sectionHeaderRow);

      // Add the regular data rows for the section
      section.rows.forEach((row) => {
        const rowData = {};
        for (const col of columns) {
          const cellData = row.data[col.id];
          if (
            col.name === "Attachments" &&
            cellData &&
            typeof cellData === "object"
          ) {
            // Join all URLs with newlines for multi-link support
            rowData[col.id] = Object.values(cellData)
              .filter((url) => url)
              .join("\n");
          } else {
            rowData[col.id] = cellData || "";
          }
        }
        pdfBody.push(rowData);
      });
    });

    // 4. Generate the table using autoTable.
    autoTable(doc, {
      columns: pdfColumns,
      body: pdfBody,
      willDrawCell: function (data) {
        if (data.row.raw?.isSectionHeader) {
          // Style for section header cells
          data.cell.styles.fillColor = [230, 230, 230]; // Light gray background
          data.cell.styles.textColor = [30, 30, 30]; // Dark text color
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.fontSize = 12;
          data.cell.styles.halign = "left";
          data.cell.styles.valign = "middle";
          // Remove borders to create solid bar effect
          data.cell.styles.lineWidth = 0;
          // Make the first cell span across all columns
          if (data.column.index === 0) {
            data.cell.colSpan = pdfColumns.length;
            data.cell.styles.cellPadding = {
              top: 4,
              right: 5,
              bottom: 4,
              left: 5,
            };
          }
        } else {
          // Regular data cell styles
          data.cell.styles.lineColor = [0, 0, 0];
          data.cell.styles.lineWidth = 0.1;
        }
      },
      didDrawCell: function (data) {
        // Only for body cells, not headers or section headers
        if (
          data.section === "body" &&
          columns[data.column.index].name === "Attachments" &&
          data.cell.raw
        ) {
          // Split by newlines in case of multiple URLs
          const urls = data.cell.raw.split("\n").filter(Boolean);
          let y = data.cell.y + 2; // Start a little below the top of the cell
          urls.forEach((url) => {
            // Draw the URL as blue text and add a link
            doc.setTextColor(0, 0, 255);
            doc.textWithLink(url, data.cell.x + 2, y + 5, { url });
            y += 6; // Move down for the next URL
          });
          doc.setTextColor(0, 0, 0); // Reset color
        }
      },
      headStyles: {
        fillColor: [22, 160, 133], // Teal color for the main header
        textColor: 255,
        fontStyle: "bold",
        fontSize: 10,
        halign: "center",
      },
      bodyStyles: {
        fontSize: 9,
        cellPadding: 3,
      },
      alternateRowStyles: {
        fillColor: [249, 249, 249], // Very light gray for alternate rows
      },
      startY: 30,
      margin: { top: 30, right: 10, bottom: 20, left: 10 },
      didDrawPage: function (data) {
        // Add a title to the top of each page
        doc.setFontSize(18);
        doc.setFont(undefined, "bold");
        doc.text("Audit Checklist Report", data.settings.margin.left, 20);
        // Add checklist name if available
        if (checklistName) {
          doc.setFontSize(12);
          doc.setFont(undefined, "normal");
          doc.text(checklistName, data.settings.margin.left, 26);
        }
      },
    });

    // 5. Save the generated PDF file.
    doc.save(`${checklistName || "checklist"}_report.pdf`);
  };

  const addColumn = () => {
    if (shouldHideStructureControls) return;
    setColumns([...columns, { id: uuidv4(), name: "New Column" }]);
  };

  const removeColumn = (columnId) => {
    if (shouldHideStructureControls) return;
    setColumns(columns.filter((col) => col.id !== columnId));
    setSections(
      sections.map((section) => ({
        ...section,
        rows: section.rows.map((row) => {
          const updatedData = { ...row.data };
          delete updatedData[columnId];
          return { ...row, data: updatedData };
        }),
      }))
    );
  };

  const addRowToSection = (sectionId) => {
    if (shouldHideStructureControls) return;
    const newRow = { id: uuidv4(), data: {} };
    setSections(
      sections.map((section) =>
        section.id === sectionId
          ? { ...section, rows: [...section.rows, newRow] }
          : section
      )
    );
  };

  const removeRowFromSection = (sectionId, rowId) => {
    if (shouldHideStructureControls) return;
    setSections(
      sections.map((section) =>
        section.id === sectionId
          ? { ...section, rows: section.rows.filter((row) => row.id !== rowId) }
          : section
      )
    );
  };

  const addSection = () => {
    if (shouldHideStructureControls) return;
    const newSection = {
      id: uuidv4(),
      title: "New Section",
      type: String.fromCharCode(65 + sections.length), // Auto-increment section type (A, B, C...)
      rows: [{ id: uuidv4(), data: {} }],
    };
    setSections([...sections, newSection]);
  };

  const removeSection = (sectionId) => {
    if (shouldHideStructureControls) return;
    setSections(sections.filter((section) => section.id !== sectionId));
  };

  const updateSectionTitle = (sectionId, newTitle) => {
    setSections(
      sections.map((section) =>
        section.id === sectionId ? { ...section, title: newTitle } : section
      )
    );
  };

  const handleImageUpload = async (e) => {
    if (shouldHideStructureControls) return;
    const file = e.target.files[0];
    if (file) {
      try {
        const uniqueFileName = `${Date.now()}-${file.name}`;
        const imageRef = ref(storage, `checklist_images/${uniqueFileName}`);
        await uploadBytes(imageRef, file);
        const url = await getDownloadURL(imageRef);
        setImageUrl(url);
      } catch (error) {
        console.error("Image upload failed:", error);
        setAlertMessage("Image upload failed. Please try again.");
        setAlertSeverity("error");
        setShowAlert(true);
      }
    }
  };

  const handleAttachmentClick = (event, rowId, columnId) => {
    setCurrentRowId(rowId);
    setCurrentColumnId(columnId);
    setAttachmentDialogOpen(true);
  };

  const handleAttachmentDialogClose = () => {
    setAttachmentDialogOpen(false);
    setAttachmentUrl("");
    setAttachmentType("url");
    setUploadedImage(null);
  };

  const handleAttachmentUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
  setUploadedImage(file);
} else {
  setAlertMessage("Please select a valid file.");
  setAlertSeverity("error");
  setShowAlert(true);
}
  };

  const handleUrlInput = async () => {
    if (attachmentType === "url" && attachmentUrl && attachmentUrl.trim()) {
      // Find the next available slot for the attachment
      const currentRow = sections
        .find((section) => section.rows.some((row) => row.id === currentRowId))
        ?.rows.find((row) => row.id === currentRowId);

      const attachmentData = currentRow?.data[currentColumnId] || {};

      let nextIndex = 1;
      for (let i = 1; i <= 6; i++) {
        if (!attachmentData[`image${i}Url`]) {
          nextIndex = i;
          break;
        }
      }

      if (nextIndex > 6) {
        nextIndex = 1;
      }

      const urlKey = `image${nextIndex}Url`;

      setSections(
        sections.map((section) => ({
          ...section,
          rows: section.rows.map((r) =>
            r.id === currentRowId
              ? {
                ...r,
                data: {
                  ...r.data,
                  [currentColumnId]: {
                    ...r.data[currentColumnId],
                    [urlKey]: attachmentUrl.trim(),
                  },
                },
              }
              : r
          ),
        }))
      );

      setAttachmentUrl("");
      handleAttachmentDialogClose();
    } else if (
  (attachmentType === "image" || attachmentType === "file") &&
  uploadedImage
) {
  try {
    const extension = uploadedImage.name.split(".").pop();
    
    const safeTenantId = tenantId || "default";
    
    const renamedFileName = `OVAudit_${safeTenantId}_EA.${extension}`;
    
    const storagePath = `audit/${safeTenantId}/EA/${renamedFileName}`;
    
    console.log(`${renamedFileName} file uploaded`);
    
    const imageRef = ref(storage, storagePath);

    await uploadBytes(imageRef, uploadedImage);

    const url = await getDownloadURL(imageRef);

    const currentRow = sections
      .find((section) => section.rows.some((row) => row.id === currentRowId))
      ?.rows.find((row) => row.id === currentRowId);

    const attachmentData = currentRow?.data[currentColumnId] || {};

    let nextIndex = 1;

    for (let i = 1; i <= 6; i++) {
      const checkKey =
        attachmentType === "image"
          ? `image${i}Url`
          : `file${i}Url`;

      if (!attachmentData[checkKey]) {
        nextIndex = i;
        break;
      }
    }

    if (nextIndex > 6) {
      nextIndex = 1;
    }

    const urlKey =
      attachmentType === "image"
        ? `image${nextIndex}Url`
        : `file${nextIndex}Url`;

    setSections(
      sections.map((section) => ({
        ...section,
        rows: section.rows.map((r) =>
          r.id === currentRowId
            ? {
                ...r,
                data: {
                  ...r.data,
                  [currentColumnId]: {
                    ...r.data[currentColumnId],
                    [urlKey]: url,
                  },
                },
              }
            : r
        ),
      }))
    );

    setUploadedImage(null);

    handleAttachmentDialogClose();

    setAlertMessage(
      attachmentType === "image"
        ? "Image uploaded successfully!"
        : "File uploaded successfully!"
    );

    setAlertSeverity("success");

    setShowAlert(true);

  } catch (error) {
    console.error("Attachment upload failed:", error);

    setAlertMessage("Attachment upload failed. Please try again.");

    setAlertSeverity("error");

    setShowAlert(true);
  }
}
  };

  const removeImage = () => {
    if (shouldHideStructureControls) return;
    setImageUrl("");
  };

  const removeAttachment = (rowId, columnId, attachmentType, index) => {
    setSections(
      sections.map((section) => ({
        ...section,
        rows: section.rows.map((r) =>
          r.id === rowId
            ? {
              ...r,
              data: {
                ...r.data,
                [columnId]: {
                  ...r.data[columnId],
                  [`${attachmentType.toLowerCase()}${index}Url`]: "",
                },
              },
            }
            : r
        ),
      }))
    );
  };

  const handleSave = async () => {
    try {
      if (!checklistName.trim()) {
        setAlertMessage("Please enter a checklist name");
        setAlertSeverity("error");
        setShowAlert(true);
        return;
      }

      const convertedRows = [];
      sections.forEach((section) => {
        convertedRows.push({
          id: uuidv4(),
          data: {
            isSectionHeader: true,
            sectionTitle: section.title,
            sectionType: section.type,
          },
        });

        section.rows.forEach((row) => {
          convertedRows.push({
            ...row,
            data: row.data || {},
          });
        });
      });

      const checklistData = {
        name: checklistName,
        description,
        remarks,
        imageUrl,
        columns,
        sections,
        rows: convertedRows,
        updatedAt: new Date().toISOString(),
      };

      if (selectedChecklist) {
        const collectionName =
          selectedChecklist.assignedTo || isAssignedChecklist
            ? "assignedChecklists"
            : "auditchecklists";

        await updateDoc(
          doc(db, collectionName, selectedChecklist.id),
          checklistData
        );

        setAlertMessage("Checklist updated successfully!");
        setAlertSeverity("success");
        setShowAlert(true);

        setTimeout(() => {
          onClose();
        }, 1000);
      } else {
        // 🚨 START OF DELETED COOKIE LOGIC 🚨
        // We replace this with safe, static data since cookies are removed.
        // 🚨 END OF DELETED COOKIE LOGIC 🚨

        const checklistDataToSave = {
          ...checklistData,
          createdBy: currentUser?.email || email,
          createdAt: new Date().toISOString(),
        };

        // The logic for tenantId and superAdmin must now rely on non-cookie data
        checklistDataToSave.tenantId = tenantId || "default";

        await addDoc(collection(db, "auditchecklists"), checklistDataToSave);

        setAlertMessage("Checklist created successfully!");
        setAlertSeverity("success");
        setShowAlert(true);

        setTimeout(() => {
          onClose();
        }, 1000);
      }
    } catch (error) {
      console.error("Error saving checklist:", error);
      setAlertMessage("Error saving checklist. Please try again.");
      setAlertSeverity("error");
      setShowAlert(true);
    }
  };

  const updateCellData = (sectionId, rowId, columnId, value) => {
    setSections(
      sections.map((section) =>
        section.id === sectionId
          ? {
            ...section,
            rows: section.rows.map((r) =>
              r.id === rowId
                ? { ...r, data: { ...r.data, [columnId]: value } }
                : r
            ),
          }
          : section
      )
    );
  };

  const renderCellContent = (col, row, sectionId) => {
    if (!row.data) {
      row.data = {};
    }

    if (col.name === "Risk Zone R/Y/G") {
      const currentValue = row.data[col.id] || "";

      return (
        <FormControl fullWidth size="small">
          <Select
            value={currentValue}
            onChange={(e) =>
              updateCellData(sectionId, row.id, col.id, e.target.value)
            }
            displayEmpty
            renderValue={(selected) => {
              if (!selected) {
                return (
                  <Typography variant="body2" color="textSecondary">
                    Select
                  </Typography>
                );
              }

              const getChipColor = (value) => {
                switch (value) {
                  case "Red":
                    return { backgroundColor: "#f44336", color: "white" };
                  case "Yellow":
                    return { backgroundColor: "#ff9800", color: "white" };
                  case "Green":
                    return { backgroundColor: "#4caf50", color: "white" };
                  default:
                    return { backgroundColor: "#e0e0e0", color: "black" };
                }
              };

              return (
                <Chip
                  label={selected}
                  size="small"
                  sx={getChipColor(selected)}
                />
              );
            }}
            sx={{
              "& .MuiSelect-select": {
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              },
            }}
          >
            <MenuItem value="">
              <Typography variant="body2" color="textSecondary">
                Select
              </Typography>
            </MenuItem>
            <MenuItem value="Red">
              <Chip
                label="Red"
                size="small"
                sx={{
                  backgroundColor: "#f44336",
                  color: "white",
                  minWidth: "60px",
                }}
              />
            </MenuItem>
            <MenuItem value="Yellow">
              <Chip
                label="Yellow"
                size="small"
                sx={{
                  backgroundColor: "#ff9800",
                  color: "white",
                  minWidth: "60px",
                }}
              />
            </MenuItem>
            <MenuItem value="Green">
              <Chip
                label="Green"
                size="small"
                sx={{
                  backgroundColor: "#4caf50",
                  color: "white",
                  minWidth: "60px",
                }}
              />
            </MenuItem>
          </Select>
        </FormControl>
      );
    }

    if (col.name === "Attachments") {
      const attachmentData = row.data[col.id] || {};
      const attachments = [];

      for (let i = 1; i <= 6; i++) {
        const urlKey = `image${i}Url`;
        if (attachmentData[urlKey]) {
          attachments.push({
            index: i,
            url: attachmentData[urlKey],
            type: "Image",
          });
        }
      }

      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            minWidth: "150px",
            padding: "8px",
          }}
        >
          <IconButton
            onClick={(e) => handleAttachmentClick(e, row.id, col.id)}
            color="primary"
            size="small"
            sx={{ alignSelf: "flex-start" }}
          >
            <AttachFile />
          </IconButton>

          {attachments.length > 0 && (
            <Box
              sx={{
                width: "100%",
                border: "1px solid #eee",
                borderRadius: "4px",
                padding: "8px",
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: "bold", marginBottom: "4px" }}
              >
                Attachments
              </Typography>
              <Box
                component="ol"
                sx={{
                  paddingLeft: "20px",
                  margin: 0,
                  "& li": {
                    marginBottom: "4px",
                    wordBreak: "break-all",
                  },
                }}
              >
                {attachments.map((attachment) => {
                  const isImage = attachment.url.match(/\.(jpg|jpeg|png|gif|bmp|webp)$/i);
                  return (
                    <Box component="li" key={`${attachment.type}${attachment.index}`}>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Typography variant="body2" sx={{ flexGrow: 1 }}>
                            <a
                              href={attachment.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: "#1976d2", textDecoration: "none" }}
                            >
                              {isImage ? `Image ${attachment.index}` : attachment.url}
                            </a>
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() =>
                              removeAttachment(
                                row.id,
                                col.id,
                                attachment.type,
                                attachment.index
                              )
                            }
                            sx={{ padding: "4px", marginLeft: "4px" }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Box>
                        {isImage && (
                          <Box sx={{
                            display: "flex",
                            justifyContent: "center",
                            border: "1px solid #ddd",
                            borderRadius: "4px",
                            padding: "4px",
                            backgroundColor: "#f9f9f9"
                          }}>
                            <img
                              src={attachment.url}
                              alt={`Attachment ${attachment.index}`}
                              style={{
                                maxWidth: "100px",
                                maxHeight: "80px",
                                objectFit: "contain"
                              }}
                              onError={(e) => {
                                e.target.style.display = "none";
                              }}
                            />
                          </Box>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}
        </Box>
      );
    }

    return (
      <TextField
        value={row.data[col.id] || ""}
        onChange={(e) =>
          updateCellData(sectionId, row.id, col.id, e.target.value)
        }
        fullWidth
        size="small"
        multiline={
          col.name === "Guideline prompts" ||
          col.name === "Observation" ||
          col.name === "Compliance details"
        }
        maxRows={col.name === "Guideline prompts" ? 4 : 2}
      />
    );
  };

  // Bulk upload handler
  const handleBulkUploadExcel = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      const bstr = evt.target.result;
      const wb = XLSX.read(bstr, { type: "binary" });
      const wsname = wb.SheetNames[0];
      const ws = wb.Sheets[wsname];
      const data = XLSX.utils.sheet_to_json(ws, { header: 1 });
      if (data.length < 2) return;
      // Map columns
      const header = data[0].map((h) => (h || "").toString().trim());
      // Map to your expected columns
      const colMap = {
        section: header.findIndex((h) => h.toLowerCase().includes("section")),
        sr: header.findIndex((h) => h.toLowerCase().includes("sr")),
        req: header.findIndex((h) => h.toLowerCase().includes("mmes")),
        guideline: header.findIndex((h) =>
          h.toLowerCase().includes("guideline")
        ),
        prompts: header.findIndex((h) => h.toLowerCase().includes("prompts")),
        observation: header.findIndex((h) =>
          h.toLowerCase().includes("observation")
        ),
        compliance: header.findIndex((h) =>
          h.toLowerCase().includes("compliance")
        ),
        maxScore: header.findIndex((h) => h.toLowerCase().includes("max.")),
        auditScore: header.findIndex((h) => h.toLowerCase().includes("audit")),
        risk: header.findIndex((h) => h.toLowerCase().includes("risk")),
        attachments: header.findIndex((h) =>
          h.toLowerCase().includes("attachment")
        ),
      };
      // Build columns for the table
      const newColumns = [
        { id: uuidv4(), name: "Sr#" },
        { id: uuidv4(), name: "MMES Requirements of 3P" },
        { id: uuidv4(), name: "Guideline prompts" },
        { id: uuidv4(), name: "Observation" },
        { id: uuidv4(), name: "Compliance details" },
        { id: uuidv4(), name: "Max. score" },
        { id: uuidv4(), name: "Audit score" },
        { id: uuidv4(), name: "Risk Zone R/Y/G" },
        { id: uuidv4(), name: "Attachments" },
      ];
      setColumns(newColumns);
      // Map rows to sections
      const sectionMap = {};
      for (let i = 1; i < data.length; i++) {
        const row = data[i];
        const sectionName = row[colMap.section] || "General";
        if (!sectionMap[sectionName]) {
          sectionMap[sectionName] = {
            id: uuidv4(),
            title: sectionName,
            type:
              Object.keys(sectionMap).length === 0
                ? "A"
                : String.fromCharCode(65 + Object.keys(sectionMap).length),
            rows: [],
          };
        }
        // Map row data to columns
        const rowData = {};
        newColumns.forEach((col, idx) => {
          let val = "";
          switch (col.name) {
            case "Sr#":
              val = row[colMap.sr];
              break;
            case "MMES Requirements of 3P":
              val = row[colMap.req];
              break;
            case "Guideline prompts":
              val = row[colMap.guideline];
              break;
            case "Observation":
              val = row[colMap.observation];
              break;
            case "Compliance details":
              val = row[colMap.compliance];
              break;
            case "Max. score":
              val = row[colMap.maxScore];
              break;
            case "Audit score":
              val = row[colMap.auditScore];
              break;
            case "Risk Zone R/Y/G":
              val = row[colMap.risk];
              break;
            case "Attachments":
              val = row[colMap.attachments];
              break;
            default:
              val = "";
          }
          rowData[col.id] = val || "";
        });
        sectionMap[sectionName].rows.push({ id: uuidv4(), data: rowData });
      }
      setSections(Object.values(sectionMap));
      setAlertMessage("Bulk upload successful! Please review before saving.");
      setAlertSeverity("success");
      setShowAlert(true);
    };
    reader.readAsBinaryString(file);
  };

  return (
    <Box
      sx={{
        padding: "2rem",
        background: "white",
        borderRadius: "8px",
        width: "95%",
        maxWidth: "1400px",
        margin: "0 auto",
        overflowY: "auto",
        maxHeight: "90vh",
      }}
    >
      <Typography variant="h5" gutterBottom>
        {selectedChecklist ? "Edit Audit Checklist" : "Create Audit Checklist"}
      </Typography>

      {shouldHideStructureControls && (
        <Alert severity="info" sx={{ marginBottom: "1rem" }}>
          Structure editing is disabled for assigned checklists. You can only
          edit cell content, name, description, and remarks.
        </Alert>
      )}

      <TextField
        label="Checklist Name"
        variant="outlined"
        value={checklistName}
        onChange={(e) => setChecklistName(e.target.value)}
        fullWidth
        sx={{ marginBottom: "1rem" }}
        required
      />

      <TextField
        label="Description"
        variant="outlined"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        fullWidth
        multiline
        rows={3}
        sx={{ marginBottom: "1rem" }}
      />

      {sections.map((section, sectionIndex) => (
        <Box key={section.id} sx={{ marginBottom: "2rem" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              marginBottom: "0px",
              padding: "8px 16px",
              backgroundColor: "#ffff00",
              border: "1px solid #ddd",
              borderBottom: "none",
              fontWeight: "bold",
              textAlign: "center",
            }}
          >
            <Typography
              variant="body1"
              sx={{
                fontWeight: "bold",
                marginRight: "1rem",
                color: "#000",
              }}
            >
              {section.type}
            </Typography>
            <TextField
              value={section.title}
              onChange={(e) => updateSectionTitle(section.id, e.target.value)}
              size="small"
              disabled={shouldHideStructureControls}
              variant="standard"
              sx={{
                flexGrow: 1,
                "& .MuiInputBase-input": {
                  fontWeight: "bold",
                  fontSize: "1rem",
                  textAlign: "center",
                  color: "#000",
                },
                "& .MuiInput-underline:before": {
                  borderBottom: "none",
                },
                "& .MuiInput-underline:after": {
                  borderBottom: "none",
                },
                "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                  borderBottom: "none",
                },
              }}
            />
            {!shouldHideStructureControls && sections.length > 1 && (
              <IconButton
                onClick={() => removeSection(section.id)}
                size="small"
                sx={{ marginLeft: "1rem" }}
                color="error"
              >
                <Delete />
              </IconButton>
            )}
          </Box>

          <Box sx={{ overflowX: "auto", marginBottom: "1rem" }}>
            <table
              style={{
                borderCollapse: "collapse",
                width: "100%",
                minWidth: "1200px",
              }}
            >
              <thead>
                <tr>
                  {columns.map((col, colIndex) => (
                    <th
                      key={col.id}
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        minWidth:
                          col.name === "Attachments"
                            ? "180px"
                            : col.name === "Guideline prompts"
                              ? "300px"
                              : col.name === "Observation"
                                ? "200px"
                                : col.name === "MMES Requirements of 3P"
                                  ? "250px"
                                  : "120px",
                        backgroundColor: "#f5f5f5",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          flexDirection: "column",
                          gap: 1,
                        }}
                      >
                        <TextField
                          value={col.name}
                          onChange={(e) =>
                            setColumns(
                              columns.map((c) =>
                                c.id === col.id
                                  ? { ...c, name: e.target.value }
                                  : c
                              )
                            )
                          }
                          fullWidth
                          size="small"
                          disabled={shouldHideStructureControls}
                          variant="standard"
                          sx={{
                            "& .MuiInputBase-input": {
                              fontWeight: "bold",
                              textAlign: "center",
                              fontSize: "0.875rem",
                            },
                            "& .MuiInput-underline:before": {
                              borderBottom: "none",
                            },
                            "& .MuiInput-underline:after": {
                              borderBottom: "none",
                            },
                            "& .MuiInput-underline:hover:not(.Mui-disabled):before":
                            {
                              borderBottom: "none",
                            },
                          }}
                        />
                        {!shouldHideStructureControls &&
                          columns.length > 9 &&
                          sectionIndex === 0 && (
                            <IconButton
                              onClick={() => removeColumn(col.id)}
                              size="small"
                            >
                              <Delete />
                            </IconButton>
                          )}
                      </Box>
                    </th>
                  ))}
                  {!shouldHideStructureControls && sectionIndex === 0 && (
                    <th
                      style={{ minWidth: "120px", backgroundColor: "#f5f5f5" }}
                    >
                      <Button
                        variant="outlined"
                        startIcon={<Add />}
                        onClick={addColumn}
                        size="small"
                      >
                        Add Column
                      </Button>
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {section.rows &&
                  section.rows.map((row, rowIndex) => (
                    <tr key={row.id}>
                      {columns.map((col) => (
                        <td
                          key={col.id}
                          style={{
                            border: "1px solid #ddd",
                            padding: "8px",
                            textAlign: "center",
                            verticalAlign: "top",
                          }}
                        >
                          {renderCellContent(col, row, section.id)}
                        </td>
                      ))}
                      {!shouldHideStructureControls && sectionIndex === 0 && (
                        <td
                          style={{ textAlign: "center", verticalAlign: "top" }}
                        >
                          <IconButton
                            onClick={() =>
                              removeRowFromSection(section.id, row.id)
                            }
                            size="small"
                          >
                            <Delete />
                          </IconButton>
                        </td>
                      )}
                    </tr>
                  ))}
              </tbody>
            </table>
          </Box>

          {!shouldHideStructureControls && (
            <Box sx={{ display: "flex", gap: 1, marginBottom: "1rem" }}>
              <Button
                variant="outlined"
                startIcon={<Add />}
                onClick={() => addRowToSection(section.id)}
                size="small"
              >
                Add Row to Section {section.type}
              </Button>
            </Box>
          )}
        </Box>
      ))}

      {!shouldHideStructureControls && (
        <Box sx={{ display: "flex", gap: 1, marginBottom: "1rem" }}>
          <Button variant="outlined" startIcon={<Add />} onClick={addSection}>
            Add New Section
          </Button>
        </Box>
      )}

      <Dialog
        open={attachmentDialogOpen}
        onClose={handleAttachmentDialogClose}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Add Attachment</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Choose attachment type and add your content:
          </DialogContentText>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <Select
              value={attachmentType}
              onChange={(e) => setAttachmentType(e.target.value)}
              displayEmpty
            >
              <MenuItem value="url">URL Link</MenuItem>
              <MenuItem value="image">Upload Image</MenuItem>
              <MenuItem value="file">Upload File</MenuItem>
            </Select>
          </FormControl>

          {attachmentType === "url" && (
            <TextField
              autoFocus
              margin="dense"
              label="Attachment URL"
              type="url"
              fullWidth
              variant="outlined"
              value={attachmentUrl}
              onChange={(e) => setAttachmentUrl(e.target.value)}
              placeholder="https://example.com/image.jpg"
            />
          )}

          {(attachmentType === "image" || attachmentType === "file") && (
            <Box sx={{ mt: 2 }}>
              <input
                accept={attachmentType === "image" ? "image/*" : ".pdf,.doc,.docx,.xls,.xlsx,.txt,image/*"}
                id="attachment-image-upload"
                type="file"
                style={{ display: "none" }}
                onChange={handleAttachmentUpload}
              />
              <label htmlFor="attachment-image-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<CloudUpload />}
                  fullWidth
                >
                  {attachmentType === "image" ? "Choose Image File" : "Choose File"}
                </Button>
              </label>
              {uploadedImage && (
                <Box sx={{ mt: 2, textAlign: "center" }}>
                  <Typography variant="body2" color="textSecondary">
                    Selected: {uploadedImage.name}
                  </Typography>
                  {attachmentType === "image" ? (
  <img
    src={URL.createObjectURL(uploadedImage)}
    alt="Preview"
    style={{
      maxWidth: "200px",
      maxHeight: "200px",
      marginTop: "8px",
      border: "1px solid #ddd",
      borderRadius: "4px",
    }}
  />
) : (
  <Typography variant="body2" sx={{ mt: 1 }}>
    File ready to upload: {uploadedImage.name}
  </Typography>
)}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAttachmentDialogClose}>Cancel</Button>
          <Button
            onClick={handleUrlInput}
            color="primary"
            disabled={
  (attachmentType === "url" && !attachmentUrl.trim()) ||
  ((attachmentType === "image" || attachmentType === "file") && !uploadedImage)
}
          >
            Add Attachment
          </Button>
        </DialogActions>
      </Dialog>

      {!shouldHideStructureControls && (
        <Box sx={{ marginBottom: "1rem" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              style={{ display: "none" }}
              id="main-image-upload"
            />
            {/* <label htmlFor="main-image-upload">
							<Button variant="outlined" component="span" startIcon={<CloudUpload />}>
								Upload Main Image
							</Button>
						</label> */}
          </Box>
          {imageUrl && (
            <Box sx={{ marginTop: "1rem" }}>
              <img
                src={imageUrl}
                alt="Uploaded"
                style={{ maxWidth: "150px", marginBottom: "1rem" }}
              />
              <Button
                variant="outlined"
                color="secondary"
                onClick={removeImage}
              >
                Remove Image
              </Button>
            </Box>
          )}
        </Box>
      )}

      {shouldHideStructureControls && imageUrl && (
        <Box sx={{ marginBottom: "1rem" }}>
          <Typography variant="subtitle2" gutterBottom>
            Main Image:
          </Typography>
          <img
            src={imageUrl}
            alt="Main checklist image"
            style={{ maxWidth: "150px", marginBottom: "1rem" }}
          />
        </Box>
      )}

      <TextField
        label="Remarks"
        variant="outlined"
        value={remarks}
        onChange={(e) => setRemarks(e.target.value)}
        fullWidth
        multiline
        rows={3}
        sx={{ marginBottom: "1rem" }}
      />

      <Box sx={{ display: "flex", gap: 2 }}>
        <Button variant="contained" color="primary" onClick={handleSave}>
          {selectedChecklist ? "Update" : "Save"}
        </Button>
        <Button variant="outlined" color="secondary" onClick={onClose}>
          Cancel
        </Button>
        {/* New button to trigger the PDF export */}
        {/* removing the export pdf button 14/7/2004 */}
        {/* <Button
					variant="contained"
					color="success"
					startIcon={<PictureAsPdf />}
					onClick={handleExportPdf}
				>
					Export to PDF
				</Button> */}
      </Box>

      {/* Bulk Upload via Excel */}
      {!shouldHideStructureControls && (
        <Box sx={{ display: "flex", gap: 2, marginBottom: "1rem" }}>
          <input
            type="file"
            accept=".xlsx, .xls"
            style={{ display: "none" }}
            id="bulk-upload-excel"
            onChange={handleBulkUploadExcel}
          />
          <label htmlFor="bulk-upload-excel">
            <Button
              variant="outlined"
              component="span"
              startIcon={<CloudUpload />}
            >
              Bulk Upload via Excel
            </Button>
          </label>
        </Box>
      )}

      <Snackbar
        open={showAlert}
        autoHideDuration={4000}
        onClose={() => setShowAlert(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={() => setShowAlert(false)} severity={alertSeverity}>
          {alertMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ChecklistForm;